import { driver, type Config, type DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import type { TourDefinition, TourRunner, TourStep } from './types';

/**
 * The real {@link TourRunner}: renders a tour with `driver.js` (item 7.6).
 *
 * This is the ONLY module that imports `driver.js`, so the rest of the framework
 * (catalogue, provider, storage) stays library-agnostic and unit-testable without
 * a DOM overlay. `driver.js` is an npm dependency bundled by Vite — its script and
 * CSS are inlined into our own bundle, so nothing is fetched from a CDN (the CSP
 * forbids external scripts).
 *
 * Accessibility & motion: `driver.js` renders keyboard-navigable popovers with
 * focus handling built in. When the caller reports reduced-motion we disable the
 * highlight animation and smooth scrolling so the tour respects the user's OS
 * setting rather than animating regardless.
 */

/** Map one framework step onto a `driver.js` step. */
function toDriveStep(step: TourStep): DriveStep {
  const popover = {
    title: step.title,
    description: step.description,
    side: step.side ?? 'bottom',
    align: step.align ?? 'start',
  } as const;

  // Omit `element` entirely for centred (intro/outro) steps — under
  // exactOptionalPropertyTypes it may not be present-but-undefined.
  return step.element === undefined ? { popover } : { element: step.element, popover };
}

/**
 * Run a tour with `driver.js`. Steps whose target element is not currently in the
 * DOM are dropped so a missing affordance never aborts the whole tour; element-less
 * (intro/outro) steps are always kept.
 */
export const driverRunner: TourRunner = (tour: TourDefinition, { reducedMotion, onClose }) => {
  const steps = tour.steps
    .filter((step) => step.element === undefined || document.querySelector(step.element) !== null)
    .map(toDriveStep);

  if (steps.length === 0) {
    onClose?.();
    return;
  }

  const config: Config = {
    steps,
    animate: !reducedMotion,
    smoothScroll: !reducedMotion,
    showProgress: steps.length > 1,
    allowClose: true,
    overlayOpacity: 0.6,
    // Hooks the design-system theme in index.css (both light and dark).
    popoverClass: 'deployhub-tour',
    nextBtnText: 'Next',
    prevBtnText: 'Back',
    doneBtnText: 'Done',
    // `onDestroyed` fires exactly once when the tour ends for ANY reason
    // (completed, skipped, Esc, or backdrop click), so it is the single place to
    // record the tour as seen.
    onDestroyed: () => onClose?.(),
  };

  driver(config).drive();
};
